import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Select,
  DatePicker,
  Input,
  message,
  Typography,
  Tag,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api.service';
import assetDepreciationService, {
  type DepreciationLinePreview,
  type DepreciationPreview,
  type DepreciationRun,
  type DepreciationLine,
  type DepreciationProjectSplit,
} from '@/services/asset-depreciation.service';
import assetAccountingDefaultsService from '@/services/asset-accounting-defaults.service';
import assetService from '@/services/asset.service';
import { buildAssetIdToLabelMap, formatJournalVoucherLabel, resolveAssetDisplayFromMap } from '@/utils/assetDisplay';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const { TextArea } = Input;
const { Text } = Typography;

interface CoaRow {
  id: string;
  account_code: string;
  account_name: string;
  is_group: boolean;
  account_type: string;
}

const AssetDepreciationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin || user?.permissions?.includes('asset.depreciation.view') || user?.permissions?.includes('asset.view')
  );
  const canRun = Boolean(
    user?.is_super_admin || user?.permissions?.includes('asset.depreciation.run') || user?.permissions?.includes('asset.edit')
  );

  const [preview, setPreview] = useState<DepreciationPreview | null>(null);
  const [previewPeriod, setPreviewPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [previewRange, setPreviewRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [runForm] = Form.useForm();
  const [selectedRun, setSelectedRun] = useState<DepreciationRun | null>(null);
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const { data: coa = [] } = useQuery({
    queryKey: ['coa', 'depr'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const data = (res as { data?: unknown })?.data ?? res;
      return Array.isArray((data as { data?: CoaRow[] })?.data)
        ? (data as { data: CoaRow[] }).data
        : Array.isArray(data)
          ? (data as CoaRow[])
          : [];
    },
    enabled: canView,
  });

  const leafAccounts = useMemo(() => coa.filter((a) => !a.is_group), [coa]);

  const { data: defaults } = useQuery({
    queryKey: ['asset-accounting-defaults'],
    queryFn: () => assetAccountingDefaultsService.get(),
    enabled: canView,
  });

  useEffect(() => {
    if (!defaults) return;
    runForm.setFieldsValue({
      expense_account_id: defaults.depreciation_expense_account_id ?? undefined,
      accum_account_id: defaults.accumulated_depreciation_account_id ?? undefined,
      period_start: dayjs().startOf('month'),
      period_end: dayjs().endOf('month'),
      period_type: 'monthly',
    });
  }, [defaults, runForm]);

  const { data: runs = [], isLoading: loadingRuns } = useQuery({
    queryKey: ['asset-depreciation-runs'],
    queryFn: () => assetDepreciationService.listRuns(),
    enabled: canView,
  });

  const previewMut = useMutation({
    mutationFn: () =>
      assetDepreciationService.preview({
        period_type: previewPeriod,
        period_start: previewRange[0].format('YYYY-MM-DD'),
        period_end: previewRange[1].format('YYYY-MM-DD'),
      }),
    onSuccess: (data) => {
      setPreview(data);
      message.success('Preview ready');
    },
    onError: (e: Error) => message.error(e?.message || 'Preview failed'),
  });

  const runMut = useMutation({
    mutationFn: (body: Parameters<typeof assetDepreciationService.run>[0]) => assetDepreciationService.run(body),
    onSuccess: (res) => {
      message.success(`Posted. Journal ${res.journal_voucher_number}`);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['asset-depreciation-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Run failed'),
  });

  const previewCols: ColumnsType<DepreciationLinePreview> = [
    { title: 'Asset', dataIndex: 'asset_code', key: 'c', width: 120 },
    {
      title: 'Group',
      dataIndex: 'asset_group_code',
      key: 'g',
      width: 100,
      render: (v: string | null) => v || '—',
    },
    { title: 'Amount', dataIndex: 'amount', key: 'a', width: 100, render: (n: number) => n.toFixed(2) },
    { title: 'Book before', dataIndex: 'book_value_before', key: 'b', width: 110, render: (n: number) => n.toFixed(2) },
    { title: 'Book after', dataIndex: 'book_value_after', key: 'f', width: 110, render: (n: number) => n.toFixed(2) },
    { title: 'Method', dataIndex: 'method_used', key: 'm', width: 100 },
    {
      title: 'GL source',
      dataIndex: 'gl_source',
      key: 'gs',
      width: 110,
      render: (v: string) => v || '—',
    },
    {
      title: 'Project split',
      key: 's',
      render: (_: unknown, r) =>
        (r.project_splits ?? []).map((s, i) => (
          <div key={i}>
            {s.project_id ? resolveProjectDisplayFromMap(projectLabels, s.project_id) : 'Unallocated'} — {s.days}d /{' '}
            {Number(s.amount).toFixed(2)}
          </div>
        )),
    },
  ];

  const runCols: ColumnsType<DepreciationRun> = [
    { title: 'Period', dataIndex: 'period_type', key: 'p', width: 100 },
    {
      title: 'Range',
      key: 'r',
      width: 200,
      render: (_: unknown, r) =>
        `${r.period_start ? String(r.period_start).slice(0, 10) : '—'} → ${r.period_end ? String(r.period_end).slice(0, 10) : '—'}`,
    },
    {
      title: 'Total',
      dataIndex: 'total_depreciation',
      key: 't',
      width: 120,
      render: (n: number) => Number(n).toFixed(2),
    },
    {
      title: 'Journal',
      key: 'j',
      ellipsis: true,
      render: (_: unknown, r: DepreciationRun) =>
        r.journal_voucher_id ? <Tag color="blue">{formatJournalVoucherLabel(r.journal_voucher_id)}</Tag> : '—',
    },
    {
      title: '',
      key: 'x',
      width: 100,
      render: (_: unknown, r: DepreciationRun) => (
        <Button type="link" size="small" onClick={() => setSelectedRun(r)}>
          Lines
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>You do not have access. Required: asset.view or asset.depreciation.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 8 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
        Back to assets
      </Button>

      <Card title="Asset depreciation" style={{ marginTop: 8 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Straight-line or WDV charge, then expense is split to projects by overlapping PMV allocation days. GL
            accounts resolve per asset: group → company defaults → optional run override.
          </Text>
          <Button type="link" onClick={() => navigate('/asset/groups')}>
            Asset groups
          </Button>
          <Button type="link" onClick={() => navigate('/asset/accounting-defaults')}>
            Company defaults
          </Button>
        </Space>

        <Space wrap style={{ marginBottom: 16 }}>
          <span>Preview period</span>
          <Select
            style={{ width: 140 }}
            value={previewPeriod}
            onChange={(v) => setPreviewPeriod(v)}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
          <DatePicker.RangePicker
            value={previewRange}
            onChange={(v) => {
              if (v?.[0] && v?.[1]) setPreviewRange([v[0], v[1]]);
            }}
            format="YYYY-MM-DD"
          />
          <Button icon={<ReloadOutlined />} loading={previewMut.isPending} onClick={() => previewMut.mutate()}>
            Preview
          </Button>
        </Space>

        {preview && (
          <div style={{ marginBottom: 24 }}>
            <Text strong>
              Preview — {preview.period_type} ({preview.period_start} → {preview.period_end})
            </Text>
            <Text style={{ marginLeft: 12 }}>Total: {preview.total.toFixed(2)}</Text>
            <Table<DepreciationLinePreview>
              size="small"
              style={{ marginTop: 8 }}
              rowKey={(r) => r.asset_id}
              dataSource={preview.lines}
              columns={previewCols}
              pagination={false}
              scroll={{ x: 900 }}
            />
          </div>
        )}

        {canRun && (
          <Card type="inner" title="Run & post to GL" style={{ marginBottom: 24 }}>
            <Form
              form={runForm}
              layout="vertical"
              initialValues={{ period_type: 'monthly' }}
              onFinish={(v) => {
                if (!v.period_start || !v.period_end) {
                  message.error('Period start and end are required');
                  return;
                }
                void runMut.mutate({
                  period_type: v.period_type,
                  expense_account_id: v.expense_account_id || null,
                  accum_account_id: v.accum_account_id || null,
                  period_start: (v.period_start as dayjs.Dayjs).format('YYYY-MM-DD'),
                  period_end: (v.period_end as dayjs.Dayjs).format('YYYY-MM-DD'),
                  notes: v.notes || null,
                });
              }}
            >
              <Form.Item name="period_type" label="Period type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'yearly', label: 'Yearly' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="expense_account_id"
                label="Expense override (optional)"
                extra="Leave blank to use each asset’s group / company defaults"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Optional override for all assets"
                  options={leafAccounts.map((a) => ({
                    value: a.id,
                    label: `${a.account_code} — ${a.account_name} (${a.account_type})`,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="accum_account_id"
                label="Accumulated depreciation override (optional)"
                extra="Leave blank to use each asset’s group / company defaults"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Optional override for all assets"
                  options={leafAccounts.map((a) => ({
                    value: a.id,
                    label: `${a.account_code} — ${a.account_name} (${a.account_type})`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="period_start" label="Period start" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="period_end" label="Period end" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={runMut.isPending}>
                Run depreciation & post
              </Button>
            </Form>
          </Card>
        )}

        <Card type="inner" title="Recent runs">
          <Table<DepreciationRun>
            size="small"
            rowKey="id"
            loading={loadingRuns}
            dataSource={runs}
            columns={runCols}
            pagination={false}
          />
        </Card>
      </Card>

      {selectedRun && (
        <Card
          title={`Run lines — ${selectedRun.period_type} ${selectedRun.period_end ? String(selectedRun.period_end).slice(0, 10) : ''}`}
          style={{ marginTop: 16 }}
          extra={
            <Button type="link" onClick={() => setSelectedRun(null)}>
              Close
            </Button>
          }
        >
          <RunLines runId={selectedRun.id} projectLabels={projectLabels} />
        </Card>
      )}
    </div>
  );
};

const RunLines: React.FC<{
  runId: string;
  projectLabels: Map<string, string>;
}> = ({ runId, projectLabels }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['asset-depreciation-run', runId],
    queryFn: () => assetDepreciationService.getRun(runId),
  });
  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'pick'],
    queryFn: () => assetService.list(),
  });
  const assetLabels = useMemo(() => buildAssetIdToLabelMap(assets), [assets]);
  const cols: ColumnsType<DepreciationLine> = [
    {
      title: 'Asset',
      dataIndex: 'asset_id',
      key: 'a',
      ellipsis: true,
      render: (id: string) => resolveAssetDisplayFromMap(assetLabels, id),
    },
    { title: 'Amount', dataIndex: 'amount', key: 'm', render: (n: number) => n.toFixed(2) },
    { title: 'Before', dataIndex: 'book_value_before', key: 'b' },
    { title: 'After', dataIndex: 'book_value_after', key: 'f' },
    { title: 'Method', dataIndex: 'method_used', key: 't' },
  ];
  const splitCols: ColumnsType<DepreciationProjectSplit> = [
    {
      title: 'Asset',
      dataIndex: 'asset_id',
      key: 'a',
      render: (id: string) => resolveAssetDisplayFromMap(assetLabels, id),
    },
    {
      title: 'Project',
      dataIndex: 'project_id',
      key: 'p',
      render: (id: string | null) => (id ? resolveProjectDisplayFromMap(projectLabels, id) : 'Unallocated'),
    },
    { title: 'Days', dataIndex: 'days', key: 'd' },
    { title: 'Amount', dataIndex: 'amount', key: 'm', render: (n: number) => Number(n).toFixed(2) },
  ];
  if (isLoading || !data) {
    return <Text type="secondary">Loading…</Text>;
  }
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Table size="small" rowKey={(r) => r.id} dataSource={data.lines} columns={cols} pagination={false} />
      <Text strong>Project splits</Text>
      <Table
        size="small"
        rowKey={(r) => r.id}
        dataSource={data.project_splits ?? []}
        columns={splitCols}
        pagination={false}
      />
    </Space>
  );
};

export default AssetDepreciationPage;
